import React, { useState, useEffect } from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "../components/ui/button";
import UserService from '../components/services/user.service.js';
import { Menu, X, LogIn, LogOut, Plus, FileText, MessageSquare, Bell } from "lucide-react";
import { toast } from '../components/ui/use-toast';
import SubscribeModal from '../components/blog/SubscribeModal';

// רכיב קטן למחליף השפה
const LanguageSwitcher = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate(); // הוספנו את ה-hook של הניווט
    const location = useLocation();

    const changeLanguage = (newLang) => {
        const currentLang = i18n.language.startsWith('he') ? 'he' : 'en';

        // אם לא משנים שפה, אל תעשה כלום
        if (newLang === currentLang) return;

        // החלפת קידומת השפה בנתיב הקיים
        // לדוגמה, אם הנתיב הוא /en/about, הוא יהפוך ל- /he/about
        const newPath = location.pathname.replace(`/${currentLang}`, `/${newLang}`);

        // קודם מנווטים לכתובת החדשה
        navigate(newPath);
        // ורק אז משנים את השפה בספרייה
        i18n.changeLanguage(newLang);
    };

    return (
        <div className="flex items-center border border-gray-300 rounded-full">
            <button
                onClick={() => changeLanguage('he')}
                className={`px-3 py-1 text-sm rounded-full transition-colors duration-300 ${i18n.language.startsWith('he') ? 'bg-[#232946] text-white' : 'text-[#232946]'}`}
            >
                {t('language.hebrew')}
            </button>
            <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 text-sm rounded-full transition-colors duration-300 ${i18n.language === 'en' ? 'bg-[#232946] text-white' : 'text-[#232946]'}`}
            >
                {t('language.english')}
            </button>
        </div>
    );
};


export default function Layout({ children }) {
    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [user, setUser] = useState(UserService.getCurrentUser());
    const [isAuthActionLoading, setIsAuthActionLoading] = useState(false);
    const [isTokenValid, setIsTokenValid] = useState(true);
    const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.documentElement.lang = i18n.language;
        document.documentElement.dir = i18n.dir(i18n.language);
        document.title = t('hero.title');
    }, [i18n, i18n.language, t]);

    const handleAuthAction = async () => {
        if (user) {
            if (!isTokenValid) {
                localStorage.removeItem('token');
                localStorage.removeItem('currentUser');
                setUser(null);
                toast({
                    variant: "destructive",
                    title: t('toast.session_expired_title'),
                    description: t('toast.session_expired_desc'),
                });
                navigate('/login');
                return;
            }
            setIsAuthActionLoading(true);
            try {
                await UserService.logout();
                setUser(null);
                toast({ title: t('toast.logout_success') });
                window.location.href = '/';
            } catch (error) {
                toast({
                    variant: "destructive",
                    title: t('toast.error_title'),
                    description: t('toast.logout_error_desc'),
                });
            } finally {
                setIsAuthActionLoading(false);
            }
        } else {
            navigate('/login');
        }
    };

    const navLinks = [
        { href: `/${i18n.language}`, label: t('nav.home') },
        { href: `/${i18n.language}/categories`, label: t('nav.categories') },
        { href: `/${i18n.language}/about`, label: t('nav.about') },
        { href: `/${i18n.language}/contact`, label: t('nav.contact') }
    ];

    const adminLinks = user?.role === 'admin' ? [
        { href: "/post-list", label: t('nav.manage_posts'), icon: <FileText className="w-4 h-4" /> },
        { href: "/create-post", label: t('nav.new_post'), icon: <Plus className="w-4 h-4" /> },
        { href: "/admin/comments", label: t('nav.manage_comments'), icon: <MessageSquare className="w-4 h-4" /> }
    ] : [];

    const allLinks = [...navLinks, ...adminLinks];

    function isTokenExpired(token) {
        if (!token) return true;
        try {
            const [, payloadBase64] = token.split('.');
            const payload = JSON.parse(atob(payloadBase64));
            return payload.exp && Date.now() >= payload.exp * 1000;
        } catch (e) {
            return true;
        }
    }

    useEffect(() => {
        const token = localStorage.getItem('token');
        setIsTokenValid(!isTokenExpired(token));
    }, [user]);

    return (
        <div dir={i18n.dir()} className="min-h-screen bg-[#f8f5f2]">
            <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;700&display=swap');
            :root { --primary: #232946; --secondary: #8890b3; --accent: #eebbc3; --background: #f8f5f2; --text: #232946; }
            body { font-family: 'Rubik', sans-serif; color: var(--text); }
            .hero-pattern { background-color: #232946; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%238890b3' fill-opacity='0.1'%3E%3Cpath opacity='.5' d='M96 95h4v1h-4v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4h-9v4h-1v-4H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15v-9H0v-1h15V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h9V0h1v15h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9h4v1h-4v9zm-1 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm9-10v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-10 0v-9h-9v9h9zm-9-10h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9zm10 0h9v-9h-9v9z'/%3E%3Cpath d='M6 5V0H5v5H0v1h5v94h1V6h94V5H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }
            .glass-nav { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(10px); border-bottom: 1px solid rgba(255, 255, 255, 0.3); }
        `}</style>

            <header className="fixed top-0 right-0 left-0 z-50 glass-nav">
                <nav className="container mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <Link to={`/${i18n.language}`} className="flex items-center gap-3">
                            <img src="/myLogo.png" alt={t('hero.title')} className="h-12 w-auto" />
                            <div>
                                <h1 className="text-2xl font-bold text-[#232946]">{t('hero.title')}</h1>
                                <p className="text-sm text-[#8890b3]">{t('hero.subtitle')}</p>
                            </div>
                        </Link>
                        {/* CHANGE: Use a larger breakpoint for the full navigation */}
                        <div className="hidden lg:flex items-center gap-4">
                            <div className="flex items-center gap-8">
                                {allLinks.map((link) => (
                                    <Link key={link.href} to={link.href} className="text-[#232946] hover:text-[#eebbc3] transition-colors flex items-center gap-2">
                                        {link.icon && React.cloneElement(link.icon, { className: "w-4 h-4" })}
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                            <Button
                                onClick={() => setIsSubscribeModalOpen(true)}
                                className="flex items-center gap-2 bg-[#eebbc3] text-[#232946] hover:bg-[#e5a9b3]"
                            >
                                <Bell className="w-4 h-4" />
                                <span>{t('nav.newsletter')}</span>
                            </Button>
                            <LanguageSwitcher />
                            <div className="h-6 w-px bg-gray-300" />
                            <Button onClick={handleAuthAction} disabled={isAuthActionLoading} variant={user ? "outline" : "default"} className={`flex items-center gap-2 ${!isTokenValid && user ? "bg-red-200 text-red-700 border-red-400" : user ? "border-[#232946] text-[#232946] hover:bg-[#232946] hover:text-white" : "bg-[#232946] text-white hover:bg-[#121629]"}`}>
                                {user ? (isTokenValid ? (<><LogOut className="w-4 h-4" /><span>{isAuthActionLoading ? t('login.logging_out') : t('login.logout')}</span></>) : (<><LogIn className="w-4 h-4" /><span>{t('login.expired')}</span></>)) : (<><LogIn className="w-4 h-4" /><span>{isAuthActionLoading ? t('status.loading') : t('login.button_login')}</span></>)}
                            </Button>
                        </div>
                        {/* CHANGE: Use a larger breakpoint for the menu button */}
                        <button className="lg:hidden text-[#232946]" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? t('nav.close_menu') : t('nav.open_menu')}>
                            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>
                    {isMenuOpen && (
                        <div className="lg:hidden py-6 border-t border-[#8890b3]/20 mt-6">
                        <div className="flex flex-col gap-6">
                    {allLinks.map((link) => (
                        <Link key={link.href} to={link.href} className="text-[#232946] hover:text-[#eebbc3] transition-colors flex items-center gap-2" onClick={() => setIsMenuOpen(false)}>
                    {link.icon && React.cloneElement(link.icon, { className: "w-5 h-5" })}
                    {link.label}
                </Link>
                ))}
                <hr className="border-t border-[#8890b3]/20" />
                <Button
                    onClick={() => { setIsSubscribeModalOpen(true); setIsMenuOpen(false); }}
                    className="w-full flex items-center justify-center gap-2 py-3 text-lg bg-[#eebbc3] text-[#232946] hover:bg-[#e5a9b3]"
                >
                    <Bell className="w-5 h-5" />
                    <span>{t('nav.newsletter')}</span>
                </Button>
                <div className="self-center">
                    <LanguageSwitcher />
                </div>
                <Button onClick={() => { handleAuthAction(); setIsMenuOpen(false); }} disabled={isAuthActionLoading} variant={user ? "outline" : "default"} className={`w-full flex items-center justify-center gap-2 py-3 ${!isTokenValid && user ? "bg-red-200 text-red-700 border-red-400" : user ? "border-[#232946] text-[#232946] hover:bg-[#232946] hover:text-white" : "bg-[#232946] text-white hover:bg-[#121629]"}`}>
                    {user ? (isTokenValid ? (<><LogOut className="w-5 h-5" /><span>{isAuthActionLoading ? t('login.logging_out') : t('login.logout')}</span></>) : (<><LogIn className="w-5 h-5" /><span>{t('login.expired')}</span></>)) : (<><LogIn className="w-5 h-5" /><span>{isAuthActionLoading ? t('status.loading') : t('login.button_register')}</span></>)}
                </Button>
        </div>
</div>
)}
</nav>
</header>

    <SubscribeModal
        open={isSubscribeModalOpen}
        onOpenChange={setIsSubscribeModalOpen}
        onSuccess={() => setIsSubscribeModalOpen(false)}
    />

    <main className="pt-32">
        {children}
    </main>

    <footer className="bg-[#232946] text-white mt-20 py-12">
        <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-3">
                    <img src="/myLogo.png" alt={t('hero.title')} className="h-10 w-auto" />
                    <p className="text-[#b8c1ec]">{t('footer.copyright', { year: new Date().getFullYear(), name: t('hero.title') })}</p>
                </div>
                <div className="flex items-center gap-6">
                    <Link to={`/${i18n.language}/about`} className="text-[#b8c1ec] hover:text-[#eebbc3] transition-colors">{t('nav.about')}</Link>
                    <Link to={`/${i18n.language}/contact`} className="text-[#b8c1ec] hover:text-[#eebbc3] transition-colors">{t('nav.contact')}</Link>
                </div>
            </div>
        </div>
    </footer>
</div>
);
}

function isTokenExpired(token) {
    if (!token) return true;
    try {
        const [, payloadBase64] = token.split('.');
        const payload = JSON.parse(atob(payloadBase64));
        return payload.exp && Date.now() >= payload.exp * 1000;
    } catch (e) {
        return true;
    }
}